import React, { useState } from "react";
import * as SubframeCore from "@subframe/core";
import { Select } from "subframe/components/Select";
import { Button } from "subframe/components/Button";
import { SurveyHistoryItems } from "./SurveyHistoryItems";
import { CategoryAnalysis } from "./CategoryAnalysis";
import EmployeeGlanceChart from "../../../components/EmployeeGlanceChart";

const OUTCOME_FILTER_OPTIONS = [
  { value: "All", label: "All Surveys" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "DISMISSED", label: "Dismissed" },
  { value: "EXPIRED", label: "Expired" },
  { value: "AT_RISK", label: "At Risk" },
];

interface SurveyHistoryProps {
  employeeId: number;
  employeeData?: {
    name: string | null;
    initials: string | null;
    status: string | null;
    jobTitle: string | null;
    location: string | null;
    state: string | null;
    hireDate: string | null;
    tenure: string | null;
    supervisor: string | null;
    department: string | null;
    phone: string | null;
    email: string | null;
    survey_timeline?: any[];
  };
  surveyHistoryData?: any[];
  surveyHistoryLoading?: boolean;
  categoryAnalysisData?: any;
  categoryAnalysisLoading?: boolean;
}

export const SurveyHistory: React.FC<SurveyHistoryProps> = ({ 
  employeeId, 
  employeeData,
  surveyHistoryData,
  surveyHistoryLoading,
  categoryAnalysisData,
  categoryAnalysisLoading
}) => {
  const [outcomeFilter, setOutcomeFilter] = useState<string>("All");

  const formatEmployeeGlanceData = (apiData: any[] = []) => {
    return apiData.map((item) => ({
      triggerDay: item.trigger_day,
      completed: item.completed,
      responseRate: item["Response Rate"],
      overallSentiment: item["Overall Sentiment"],
      sentimentScore: item["Sentiment Score"],
    }));
  };

  return (
    <div className="flex h-full w-full flex-col items-start bg-default-background">

      {/* Survey History Content */}
      <div className="flex w-full grow shrink-0 bg-neutral-50 basis-0 flex-col items-start gap-4 sm:gap-6 px-3 sm:px-4 md:px-8 lg:px-12 pt-3 pb-8 sm:pb-12">
        <div className="flex w-full flex-col items-start gap-1">
          <div className="flex w-full flex-col lg:flex-row items-start gap-4 lg:gap-6">
            {/* Milestone History */}
            <div className="transform-gpu flex w-full lg:w-[600px] xl:w-[700px] lg:flex-none flex-col items-start gap-4 rounded-lg bg-white max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Sticky Header */}
              <div className="flex w-full flex-col items-start gap-3 sm:gap-4 px-3 sm:px-4 lg:px-6 py-4 sm:py-6 bg-white rounded-t-lg">
                <span className="text-subheader font-subheader text-default-font">
                  Milestone History
                </span>
                <EmployeeGlanceChart
                  data={formatEmployeeGlanceData(employeeData?.survey_timeline) as any}
                  isLoading={false}
                  showHeading={false}
                />
                <div className="flex w-full flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-2 px-1 py-1">
                  <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 self-stretch">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-body font-body text-default-font">
                        Show
                      </span>
                      <SubframeCore.Icon
                        className="text-body font-body text-default-font"
                        name="FeatherListFilter"
                      />
                    </div>
                    <Select
                      className="h-auto w-full sm:w-auto flex-none self-stretch relative z-50"
                      label=""
                      placeholder="All"
                      helpText=""
                      value={outcomeFilter}
                      onValueChange={(value: string) => setOutcomeFilter(value)}
                    >
                      {OUTCOME_FILTER_OPTIONS.map((option) => (
                        <Select.Item value={option.value} key={option.value}>
                          {option.label}
                        </Select.Item>
                      ))}
                    </Select>
                  </div>
                  <Button
                    variant="brand-tertiary"
                    icon="FeatherExpand"
                    className="w-full sm:w-auto"
                    onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                      // This will be handled by the SurveyHistoryItems component
                      const expandAllEvent = new CustomEvent('expandAllSurveys');
                      window.dispatchEvent(expandAllEvent);
                    }}
                  >
                    <span className="hidden sm:inline">Expand All</span>
                    <span className="sm:hidden">Expand</span>
                  </Button>
                </div>
              </div>
              
              {/* Scrollable Content */}
              <div className="relative z-10 flex w-full flex-col items-start gap-4 px-3 sm:px-4 lg:px-6 pb-4 sm:pb-6">
                {/* Survey Items */}
                <SurveyHistoryItems 
                  surveyHistory={surveyHistoryData}
                  isLoading={surveyHistoryLoading}
                  employeeId={employeeId}
                  outcomeFilter={outcomeFilter}
                />
              </div>
            </div>

            {/* Category Analysis */}
            <div className="flex w-full lg:flex-1 flex-col items-start gap-4 self-stretch rounded-lg bg-white px-3 sm:px-4 lg:px-6 py-4 sm:py-6 overflow-y-auto max-h-[calc(100vh-400px)] sm:max-h-[calc(100vh-300px)]">
              <span className="text-subheader font-subheader text-default-font">
                Category Analysis
              </span>
              <CategoryAnalysis 
                employeeId={employeeId}
                categoryAnalysisData={categoryAnalysisData}
                categoryAnalysisLoading={categoryAnalysisLoading}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}; 